import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import { PageTitle } from "../shared/Page";
import { EmptyState } from "../shared/EmptyState";
import { usePrivacyDocumentTitle } from "../shared/usePrivacyDocumentTitle";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { TenantIcon2 } from "../shared/icons";
import { Grid } from "../shared/Grid";
import { DynamicGrid } from "../dynamicGrid";
import { prefetch } from "../api/prefetch";
import {
  enterpriseCustomerOrganisationsQueryOptions,
  enterpriseCustomerQueryOptions,
  enterpriseCustomerUserSelectOptionsQueryOptions,
  type EnterpriseCustomerOrganisation,
} from "../api/enterpriseCustomer";
import { CountryValue, CreateOrganisationDialog, LanguageValue } from "./CreateOrganisationDialog";
import { OrganisationActionMenu } from "./OrganisationActionMenu";
import { OrganisationDrawer } from "./OrganisationDrawer";

const OrganisationsGridPage = () => {
  usePrivacyDocumentTitle("Sidebar.EnterpriseCustomer.Organisations");
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const organisationsQuery = useQuery(enterpriseCustomerOrganisationsQueryOptions());

  return (
    <DynamicGrid.Grid
      form="no-form"
      rowsData={organisationsQuery.data}
      getRowId={({ id }) => id}
      onRowSelect={selected => navigate(paths.enterpriseCustomerOrganisationById(selected))}
      mainColumnProps={{
        headerName: t("EnterpriseCustomer.Field.Name"),
        renderLabel: ({ organizationName }) => organizationName,
        renderIcon: ({ iconProps }) => <TenantIcon2 {...iconProps} />,
      }}
      getColumns={() => [
        {
          ...DynamicGrid.requiredColDef({
            field: "organizationNumber",
            headerName: t("EnterpriseCustomer.Field.OrganisationNumber"),
          }),
          renderCell: ({ row }) => <DynamicGrid.TextInputCell value={row.organizationNumber} />,
        },
        {
          ...DynamicGrid.requiredColDef({ field: "countryCode", headerName: t("EnterpriseCustomer.Field.Country") }),
          valueGetter: (_, row: EnterpriseCustomerOrganisation) => t(`EnterpriseCustomer.Country.${row.countryCode}`),
          renderCell: ({ row }) => <CountryValue countryCode={row.countryCode} />,
        },
        {
          ...DynamicGrid.requiredColDef({ field: "language", headerName: t("EnterpriseCustomer.Field.Language") }),
          valueGetter: (_, row: EnterpriseCustomerOrganisation) => t(`EnterpriseCustomer.Language.${row.language}`),
          renderCell: ({ row }) => <LanguageValue language={row.language} />,
        },
        {
          ...DynamicGrid.requiredColDef({
            field: "productAdmins",
            headerName: t("EnterpriseCustomer.Organisation.ProductAdmins.Label"),
          }),
          width: 260,
          valueGetter: (_, row: EnterpriseCustomerOrganisation) =>
            row.productAdmins
              .map(({ firstName, lastName }) => `${firstName} ${lastName}`)
              .join(Grid.VALUE_GETTER_DELIMITER),
          renderCell: ({ row }) => (
            <DynamicGrid.Cell withExtraSpacingTop={false}>
              <DynamicGrid.PersonCell users={row.productAdmins} />
            </DynamicGrid.Cell>
          ),
        },
      ]}
      renderActionMenu={({ row, selectedRowId }) => (
        <OrganisationActionMenu
          organisation={row}
          onDeleted={() => {
            if (selectedRowId === row.id) navigate(paths.enterpriseCustomerOrganisations());
          }}
        />
      )}
      texts={{ searchFieldPlaceholder: t("EnterpriseCustomer.Organisations.SearchFieldLabel") }}
      Toolbar={OrganisationsGridToolbar}
      Footer={OrganisationsGridFooter}
      NoRowsOverlay={NoOrganisationsOverlay}
    />
  );
};

const OrganisationsGridToolbar = () => {
  const { t } = useTranslation();
  const customerQuery = useQuery(enterpriseCustomerQueryOptions());

  return (
    <DynamicGrid.Toolbar
      pageTitle={<PageTitle title={t("Sidebar.EnterpriseCustomer.Organisations")} />}
      filterButtons={
        <>
          <DynamicGrid.ColumnFilterButton />
          <DynamicGrid.FilterButton />
        </>
      }
      actionButtons={
        customerQuery.data && (
          <DynamicGrid.CreateNewButton
            labelKey="EnterpriseCustomer.Organisations.NewOrganisation"
            renderDialog={({ onClose }) => (
              <CreateOrganisationDialog
                defaultCountryCode={customerQuery.data.countryCode}
                defaultLanguage={customerQuery.data.language}
                onClose={onClose}
              />
            )}
          />
        )
      }
    />
  );
};

const OrganisationsGridFooter = () => (
  <DynamicGrid.Footer numberOfRowsTextKey="EnterpriseCustomer.Organisations.NumberOfRows" />
);

const NoOrganisationsOverlay = () => <EmptyState labelKey="EnterpriseCustomer.Organisations.NoRows" />;

export const prefetchOrganisationsGridPage = () => {
  prefetch(enterpriseCustomerOrganisationsQueryOptions());
  prefetch(enterpriseCustomerQueryOptions());
  prefetch(enterpriseCustomerUserSelectOptionsQueryOptions());
};

export const organisationsGridPageRoutes = DynamicGrid.setupRoutes({
  gridElement: <OrganisationsGridPage />,
  rowDrawerRoutes: [{ index: true, element: <OrganisationDrawer /> }],
  gridPrefetcher: prefetchOrganisationsGridPage,
});
